<template>
  <div class="home" v-loading.fullscreen.lock="display.isLoading">
    <el-col :xs="8" :md="4" class="category-list bg-black">
      <div
        v-if="currentMode === 'DINE_IN' && currentTableNumber"
        class="category-list__item gold version"
      >
        Current table number: <strong>{{ currentTableNumber }}</strong>
      </div>
      <div
        class="category-list__item"
        :class="isSearchMode ? 'bg-gold black font-bold' : 'gold'"
        @click="onClickSearchOption"
      >
        Search for Dishes
      </div>
      <div v-for="(category, index) in categories" :key="category._id">
        <div
          class="category-list__item"
          :class="
            index === activeCategoryIndex ? 'bg-gold black font-bold' : 'gold'
          "
          @click="() => onClickCategory(index)"
        >
          {{ category.name }}
        </div>
        <div
          v-if="index === activeCategoryIndex && category.children.length > 0"
        >
          <div
            class="category-list__item child-category"
            :class="
              childCategoryIndex === activeChildCategoryIndex
                ? 'bg-gold black font-bold fade'
                : 'bg-black gold'
            "
            v-for="(childCategory, childCategoryIndex) in category.children"
            :key="childCategory._id"
            @click="() => onClickChildCategory(childCategoryIndex)"
          >
            {{ childCategory.name }}
          </div>
        </div>
      </div>
      <div class="category-list__item gray version">v{{ appVersion }}</div>
    </el-col>
    <el-col :xs="16" :md="20" class="product-list bg-white">
      <div v-if="isSearchMode">
        <el-input
          prefix-icon="el-icon-search"
          class="search-bar"
          v-model="searchText"
          clearable
          @clear="onClickClearSearchText"
        >
          <el-button
            slot="append"
            class="bg-black gold search-btn"
            @click="onClickSearch"
          >
            Search
          </el-button>
        </el-input>
      </div>
      <div v-if="products.length > 0">
        <div class="product-grid">
          <el-card
            :body-style="{ padding: '0.5rem' }"
            class="product-list__item"
            v-for="product in products"
            :key="product._id"
          >
            <el-row slot="header" :gutter="10" type="flex" align="bottom">
              <el-col :span="16">
                <h3>{{ product.name }}</h3>
              </el-col>
              <el-col :span="8">
                <div v-if="product.isDetailPageEnabled">
                  <el-button
                    size="mini"
                    class="bg-gold black"
                    @click="() => onClickDetailPage(product)"
                    >Details</el-button
                  >
                </div>
              </el-col>
            </el-row>
            <el-row
              type="flex"
              align="top"
              :gutter="10"
              :id="
                `category${product.categoryId}__childcategory${product.childCategoryId}`
              "
            >
              <el-col :span="10">
                <el-image
                  class="product-list__item__img"
                  :src="product.featuredImageUrl"
                ></el-image>
              </el-col>
              <el-col
                :span="14"
                class="product-list__item__shopping-cart-input"
              >
                <p class="product-list__item__price">
                  {{ product.unitNetPriceDisplay }}
                </p>
                <el-input-number
                  v-if="!product.isSoldOut"
                  class="cart"
                  v-model="product.shoppingCartItemQuantity"
                  :min="0"
                  :max="
                    product.package.isPackage ||
                    product.specification.isSpecificationEnabled ||
                    (product.subProducts && product.subProducts.length > 0)
                      ? 1
                      : 1024
                  "
                  @change="
                    (event) => onChangeShoppingCartItemQuantity(event, product)
                  "
                ></el-input-number>
                <strong v-if="product.isSoldOut">Sold Out</strong>
                <p class="product-list__item__sku">SKU: {{ product.sku }}</p>
                <p
                  :class="product.isDescriptionExpanded ? '' : 'clamped'"
                  class="product-list__item__description"
                  v-if="product.description"
                >
                  {{ product.description }}
                </p>
                <a
                  v-if="typeof product.isDescriptionExpanded === 'boolean'"
                  class="gold product-list__item__description__toggle"
                  @click="() => onClickDescriptionToggle(product)"
                >
                  <i
                    v-if="!product.isDescriptionExpanded"
                    class="el-icon-plus"
                  ></i>
                  <i v-else class="el-icon-minus"></i>
                  Read {{ !!product.isDescriptionExpanded ? "less" : "more" }}
                </a>
              </el-col>
            </el-row>
          </el-card>
        </div>
      </div>
      <div v-else>
        <h3 v-if="!isSearchMode" class="no-product-title">No Product</h3>
        <h3></h3>
      </div>
    </el-col>

    <!-- bottom bar -->
    <div class="bottom-bar bg-black">
      <el-row type="flex" align="middle">
        <el-col :span="12" class="white bottom-bar__col">
          <span v-if="shoppingCart.shoppingCart.lineItems">
            {{ shoppingCart.shoppingCart.lineItems.length }} items, ${{
              subtotal
            }}
          </span>
          <br />
          <delivery-indicator
            v-if="isDelivery(currentOrderMode)"
            :subtotal="Number(subtotal)"
            color="#eee"
            @clickInfo="
              display.dialog.minimumDeliveryAmountRule.isVisible = true
            "
          ></delivery-indicator>
        </el-col>
        <el-col :span="12" class="right bottom-bar__col">
          <el-button-group>
            <el-button
              size="small"
              class="bg-gold black border-black no-border-radius"
              @click="onClickShoppingCart"
              >Cart</el-button
            >
            <el-button
              size="small"
              class="bg-gold black border-black no-border-radius"
              @click="onClickCheckout"
              >Checkout</el-button
            >
          </el-button-group>
        </el-col>
      </el-row>
    </div>

    <!-- Bottom-right function buttons -->
    <el-button
      size="mini"
      class="bg-black border-black gold btn-booking side-btn"
      :class="token ? 'side-btn-second' : 'side-btn-first'"
      @click="onClickBooking"
      >Book a Table</el-button
    >
    <el-button
      v-if="!token"
      size="mini"
      class="bg-black border-black gold btn-sign-up-in side-btn side-btn-second"
      @click="onClickSignUpIn"
      >Sign In/Up</el-button
    >
    <el-button
      v-if="!featureToggle.MEMBERSHIP || !token"
      size="mini"
      class="bg-black border-black gold btn-my-order side-btn side-btn-third"
      @click="onClickMyOrder"
      >My Orders</el-button
    >
    <el-button
      v-if="featureToggle.MEMBERSHIP && token"
      size="mini"
      class="bg-black border-black gold btn-personal side-btn side-btn-third"
      @click="onClickPersonal"
      >Personal</el-button
    >
    <el-button
      size="mini"
      class="
        bg-black
        border-black
        gold
        btn-customer-service
        side-btn side-btn-fourth
      "
      @click="onClickCustomerService"
      >Contact Us</el-button
    >

    <!-- Sign up/in dialog -->
    <el-dialog
      class="sign-up-in-dialog"
      :fullscreen="true"
      :visible.sync="display.dialog.signUpIn.isVisible"
      :before-close="beforeSignUpInClose"
    >
      <h3 class="centre">Welcome to King Dynasty Online</h3>
      <div class="centre">
        <el-radio-group
          v-model="display.dialog.signUpIn.modeDisplay"
          @change="onChangeSignInUpMode"
        >
          <el-radio-button
            v-for="mode in SIGN_IN_UP_MODE"
            :key="mode.value"
            :label="mode.display"
          ></el-radio-button>
        </el-radio-group>
      </div>
      <h3 class="centre">{{ display.dialog.signUpIn.modeDisplay }}</h3>
      <el-form
        class="customer-form"
        ref="customer"
        :model="customer"
        :rules="rules.customer"
      >
        <el-row v-if="display.dialog.signUpIn.mode === 'SIGN_UP'">
          <el-col :span="12">
            <el-form-item prop="firstName">
              <el-input
                v-model="customer.firstName"
                placeholder="First Name (Required)"
              ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="surname">
              <el-input
                class="border-gold"
                v-model="customer.surname"
                placeholder="Surname (Required)"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row
          v-if="
            featureToggle.MEMBERSHIP &&
              display.dialog.signUpIn.mode === 'SIGN_UP'
          "
        >
          <el-col :span="24">
            <el-form-item prop="birthday">
              <el-date-picker
                class="full-width"
                v-model="customer.birthday"
                placeholder="Birthday (Required, to Month)"
                type="month"
                format="MMM yyyy"
              ></el-date-picker>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <el-form-item prop="phone">
              <el-input
                type="number"
                v-model="customer.phone"
                placeholder="Mobile (Required)"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="display.dialog.signUpIn.mode === 'SIGN_UP'">
          <el-col :span="24">
            <el-form-item prop="email">
              <el-input
                type="email"
                v-model="customer.email"
                placeholder="Email Address"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <el-form-item prop="password">
              <el-input
                v-model="customer.password"
                placeholder="Password (Required)"
                show-password
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <div v-if="display.dialog.signUpIn.mode === 'SIGN_UP'">
          <el-row>
            <el-col :span="24">
              <el-form-item prop="passwordConfirm">
                <el-input
                  v-model="customer.passwordConfirm"
                  placeholder="Re-enter Password (Required)"
                  show-password
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="24">
              <el-form-item prop="shippingAddress.streetAddress">
                <el-input
                  v-model="customer.shippingAddress.streetAddress"
                  placeholder="Street Address"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="12">
              <el-form-item prop="shippingAddress.city">
                <el-input
                  v-model="customer.shippingAddress.city"
                  placeholder="Suburb"
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="shippingAddress.state">
                <el-input
                  v-model="customer.shippingAddress.state"
                  placeholder="State"
                  disabled
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="12">
              <el-form-item prop="shippingAddress.country">
                <el-input
                  v-model="customer.shippingAddress.country"
                  placeholder="Country"
                  disabled
                ></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="shippingAddress.zipCode">
                <el-input
                  v-model="customer.shippingAddress.zipCode"
                  placeholder="Zip Code"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
        <div class="forgot-pwd">
          <a class="gold" @click="onClickForgotPassword">Forgot password?</a>
        </div>
        <div class="centre">
          <el-button
            v-if="display.dialog.signUpIn.mode === 'SIGN_IN'"
            class="bg-gold black"
            @click="onClickSignIn"
            >Sign In</el-button
          >
          <el-button
            v-if="display.dialog.signUpIn.mode === 'SIGN_UP'"
            class="bg-gold black"
            @click="onClickSignUp"
            >Sign Up</el-button
          >
        </div>
        <div class="centre footer-version">Version {{ appVersion }}</div>
      </el-form>
    </el-dialog>

    <!-- Complex product dialog -->
    <el-dialog
      :title="`Adding ${expandedProduct.name}`"
      :width="display.dialogWidth"
      :visible.sync="display.dialog.packageOptions.isVisible"
      @close="onClickClosePackageOption"
    >
      <div class="complex-product-details">
        <el-tabs v-model="display.dialog.packageOptions.currentTab">
          <el-tab-pane
            v-if="expandedProduct.package && expandedProduct.package.isPackage"
            label="Selections"
            name="Selections"
          >
            <div
              class="side-dialog-selection"
              v-for="(lineItemSelection,
              lineItemSelectionIndex) in expandedProduct.packageSelections"
              :key="lineItemSelectionIndex"
            >
              <h3>
                <strong>Variant {{ lineItemSelectionIndex + 1 }}</strong>
              </h3>
              <div
                v-for="(productSelection,
                productSelectionIndex) in expandedProduct.package
                  .packageOptions"
                :key="productSelectionIndex"
              >
                <p>
                  <strong>{{ productSelection.name }}</strong>
                </p>
                <el-checkbox-group
                  v-model="lineItemSelection[productSelection.name]"
                >
                  <el-checkbox
                    v-for="productOption in productSelection.options"
                    :key="productOption"
                    :label="productOption"
                    :disabled="
                      isSelectionDisabled(
                        productSelection,
                        productOption,
                        lineItemSelection
                      )
                    "
                  ></el-checkbox>
                </el-checkbox-group>
              </div>
            </div>
          </el-tab-pane>
          <el-tab-pane
            v-if="
              expandedProduct.specification &&
                expandedProduct.specification.isSpecificationEnabled
            "
            label="Specifications"
            name="Specifications"
          >
            <div class="side-dialog-selection">
              <div
                v-for="(sel, selIndex) in expandedProduct.specification
                  .specificationOptions"
                :key="selIndex"
              >
                <el-radio-group
                  v-model="expandedProduct.specificationSelectionIndex"
                >
                  <el-radio :label="selIndex">
                    {{ sel.name }} (${{ sel.unitNetPriceDisplay }})
                  </el-radio>
                </el-radio-group>
              </div>
            </div>
          </el-tab-pane>
          <el-tab-pane
            v-if="
              expandedProduct.subProducts &&
                expandedProduct.subProducts.length > 0
            "
            label="Sub Menu"
            name="Sub Menu"
          >
            <div class="side-dialog-selection">
              <el-checkbox-group
                v-model="expandedProduct.selectedSubProductNames"
              >
                <el-checkbox
                  v-for="subProduct in expandedProduct.subProducts"
                  :key="subProduct._id"
                  :label="
                    `${subProduct.name} (${subProduct.unitNetPriceDisplay})`
                  "
                ></el-checkbox>
              </el-checkbox-group>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
      <div class="centre" slot="footer">
        <el-button
          class="bg-gold black"
          @click="display.dialog.packageOptions.isVisible = false"
          >Back</el-button
        >
        <el-button
          v-if="!expandedProduct.isSoldOut"
          class="bg-black gold"
          @click="onClickComplexProductConfirm"
          >Confirm</el-button
        >
      </div>
    </el-dialog>

    <!-- Contact us dialog -->
    <el-dialog
      :width="display.dialogWidth"
      :visible.sync="display.dialog.customerService.isVisible"
    >
      <h3 class="centre">Contact Us</h3>
      <!-- <el-row :gutter="20">
        <el-col :span="10">WeChat QR Code</el-col>
        <el-col :span="12">TODO</el-col>
      </el-row>-->
      <el-row class="contact-row" :gutter="20">
        <el-col class="right" :span="10">
          <strong>WeChat ID</strong>
        </el-col>
        <el-col :span="12">kingdynastyone</el-col>
      </el-row>
      <el-row class="contact-row" :gutter="20">
        <el-col class="right" :span="10">
          <strong>Tel</strong>
        </el-col>
        <el-col :span="12">
          <a class="gold" href="tel:+61294139388">02 9413 9388</a>
          <br />
          <a class="gold" href="tel:+61406988000">0406 988 000</a>
        </el-col>
      </el-row>
      <el-row class="contact-row" :gutter="20">
        <el-col class="right" :span="10">
          <strong>Email</strong>
        </el-col>
        <el-col :span="12">
          <a class="gold" href="mailto:bookings@kingdynasty.com.au"
            >bookings@kingdynasty.com.au</a
          >
        </el-col>
      </el-row>
      <el-row class="contact-row" :gutter="20">
        <el-col class="right" :span="10">
          <strong>Opening Hours</strong>
        </el-col>
        <el-col :span="12">
          <div>
            <strong>Monday to Friday:</strong>
            <br />10:30am - 3:00pm <br />5:00pm - 9:00pm
          </div>
          <div>
            <strong>Saturday to Sunday:</strong>
            <br />10:00am - 9:00pm
          </div>
        </el-col>
      </el-row>
      <el-row class="contact-row centre">
        <el-col :span="24">
          <el-button
            class="bg-gold black"
            @click="display.dialog.customerService.isVisible = false"
            >Back</el-button
          >
        </el-col>
      </el-row>
    </el-dialog>

    <!-- Existing same-day dine-in order addition confirmation dialog -->
    <el-dialog
      :visible.sync="display.dialog.dineInAddLineItemsConfirmation.isVisible"
      title="Current Dine-In Order"
      :width="display.dialogWidth"
      class="add-item-dine-in-order-dialog"
    >
      <div v-if="currentDineInOrder">
        <p class="centre break-word">
          We've detected you have an ongoing dine-in order today:
        </p>
        <h3 class="centre break-word">{{ currentDineInOrder.orderNumber }}</h3>
        <p class="centre break-word">
          {{ currentDineInOrder.createdTimeDisplayDateOnly }}
        </p>
        <p class="centre break-word">
          Do you wish us add the items to this order?
        </p>
        <div>
          <el-row
            :gutter="20"
            v-for="item of shoppingCart.shoppingCart.lineItems"
            :key="item._id"
          >
            <el-col :span="8" class="break-word">{{ item.name }}</el-col>
            <el-col :span="7" class="break-word">
              {{ item.unitNetPriceDisplay }}
            </el-col>
            <el-col :span="2">{{ item.quantity }}</el-col>
            <el-col :span="7">${{ item.totalGrossPriceDisplay }}</el-col>
          </el-row>
        </div>
      </div>
      <div class="centre" slot="footer">
        <div>
          <el-button
            class="bg-black gold btn"
            @click="onClickAddToCurrentDineInOrder"
            >Add to Current Order</el-button
          >
        </div>
        <div>
          <el-button class="bg-gold black btn" @click="onClickPlaceNewOrder"
            >Place New Order</el-button
          >
        </div>
      </div>
    </el-dialog>

    <!-- Launch notification dialog -->
    <el-dialog
      :width="display.dialogWidth"
      :visible.sync="display.dialog.signUpNotificationDialog.isVisible"
    >
      <div>
        <h3>Welcome to King Dynasty Online contactless ordering system.</h3>
        <p>
          Now you can choose our most popular Yum Cha/A La Carte/Set Menu with
          option of Dine In/Takeaway/Delivery service, all available online!
        </p>
        <p>
          Sign up today you will get $10 voucher + free delivery fees for your
          first online order.
        </p>
        <div class="centre">
          <el-button
            class="black bg-gold"
            @click="
              display.dialog.signUpNotificationDialog.isVisible = false;
              display.dialog.signUpIn.isVisible = true;
            "
            >Sign Up/In</el-button
          >
        </div>
      </div>
    </el-dialog>

    <!-- Home options -->
    <el-dialog
      class="home-options-dlg"
      :visible.sync="display.dialog.homeOptions.isVisible"
      :width="display.dialogWidth"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <div>
        <h3 class="centre">Welcome to King Dynasty Online</h3>
        <h4 class="centre">Please Select</h4>
        <el-row :gutter="20" class="home-options">
          <el-col :span="12">
            <el-card class="box-card bg-gold centre home-option-blk">
              <div @click="onClickDineInQueue">Dine-in Queue</div>
            </el-card>
          </el-col>
          <el-col :span="12">
            <a @click="onClickTableReservation">
              <el-card class="box-card bg-gold centre">
                <div>Table Reservation</div>
              </el-card>
            </a>
          </el-col>
        </el-row>
        <el-row :gutter="20" class="home-options">
          <el-col :span="12">
            <a @click="onClickDineInOnlineOrder">
              <el-card
                class="box-card bg-gold centre home-option-blk"
                :class="isDineInEnabled ? '' : 'disabled'"
              >
                <div>Dine-in Online Order</div>
              </el-card>
            </a>
          </el-col>
          <el-col :span="12">
            <a @click="onClickTakeawayOrder">
              <el-card class="box-card bg-gold centre">
                <div>Takeaway Order</div>
              </el-card>
            </a>
          </el-col>
        </el-row>
        <el-row :gutter="20" class="home-options">
          <el-col :span="12">
            <a @click="onClickDeliveryOrder">
              <el-card class="box-card bg-gold centre">
                <div>Delivery Order</div>
              </el-card>
            </a>
          </el-col>
          <el-col :span="12">
            <a @click="onClickBecomeMember" v-if="!token">
              <el-card class="box-card bg-gold centre">
                <div>Membership Sign Up/In</div>
              </el-card>
            </a>
            <a @click="onClickWelcome" v-else>
              <el-card class="box-card bg-gold centre">
                <div>Welcome, {{ display.dialog.homeOptions.memberName }}</div>
              </el-card>
            </a>
          </el-col>
        </el-row>
      </div>
    </el-dialog>

    <!-- Minimum delivery amount rules dialog -->
    <delivery-rule-dialog
      :isVisible="display.dialog.minimumDeliveryAmountRule.isVisible"
      @close="display.dialog.minimumDeliveryAmountRule.isVisible = false"
    ></delivery-rule-dialog>

    <!-- Error dialog -->
    <el-dialog
      :visible.sync="display.dialog.error.isVisible"
      :width="display.dialogWidth"
      :title="display.dialog.error.title"
      class="error-dialog"
    >
      <div>
        <p class="centre">{{ display.dialog.error.message }}</p>
        <div class="centre">
          <el-button
            class="bg-black gold"
            @click="display.dialog.error.isVisible = false"
            >OK</el-button
          >
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// @ is an alias to /src
import Cookies from "js-cookie";
import { mapGetters } from "vuex";
import moment, { utc } from "moment";
import { anyPass, isNil, isEmpty as rIsEmpty, groupBy } from "ramda";
import { list as listCategories } from "@/api/category";
import { listByCategory, listActive } from "@/api/product";
import { signUp, getMe } from "@/api/customer";
import { getById, addLineItems } from "@/api/order";
import {
  isNotNilOrEmpty,
  isEmpty,
  calculateSubtotal,
  appVersion,
  getAttributeValue,
  setAttributeValue,
  deepClone,
  featureToggle,
  isDineInEnabled,
  isCovidCheckInEnabled,
  CONFIG_KEYS,
} from "@/utils";
import { decorateProduct } from "@/utils/product";
import { SIGN_IN_UP_MODE } from "@/utils/customer";
import { decorateShoppingCartItem } from "@/utils/shoppingCart";
import { MINIMUM_DELIVERY_AMOUNT, isDelivery } from "@/utils/order";
import { errorMessages } from "@/utils/info";
import logger from "@/utils/logger";

const name = "Home";
const log = logger(name);

const ORDER_ERROR_CODE_MAP = {
  not_found: "Order number from detail link could not be found.",
  expired: "Order link has expired.",
};

export default {
  name,
  data() {
    const validateRetypePassword = (rule, value, cb) => {
      if (value !== this.customer.password) {
        return cb(
          new Error("The re-typed password is not the same as the former one.")
        );
      }
      cb();
    };
    const isMdAndUp = window.innerWidth >= 992;
    return {
      featureToggle,

      isDineInEnabled,
      isCovidCheckInEnabled,

      categories: [],
      allProducts: [],
      products: [],
      productsShown: [],
      activeCategoryName: "",
      activeCategoryIndex: 0,
      activeChildCategoryIndex: 0,
      display: {
        isLoading: false,
        dialog: {
          customerService: { isVisible: false },
          signUpIn: {
            isVisible: false,
            mode: "SIGN_IN",
            modeDisplay: "Sign In",
          },
          packageOptions: {
            isVisible: false,
            currentTab: "Selections",
          },
          specification: { isVisible: false },
          signUpNotificationDialog: { isVisible: false },
          dineInAddLineItemsConfirmation: { isVisible: false },
          homeOptions: {
            isVisible: false,
            memberName: "",
          },
          minimumDeliveryAmountRule: {
            isVisible: false,
          },
          error: {
            isVisible: false,
            title: "",
            message: "",
          },
        },
        dialogWidth: isMdAndUp ? "60%" : "90%",
      },
      subtotal: "0.00",
      customer: {
        firstName: "",
        surname: "",
        phone: "",
        password: "",
        passwordConfirm: "",
        shippingAddress: {
          state: "NSW",
          country: "Australia",
        },
      },
      expandedProduct: {},
      currentDineInOrder: null,
      isSearchMode: false,
      searchText: "",

      appVersion,
      SIGN_IN_UP_MODE,
      rules: {
        customer: {
          firstName: [
            {
              required: true,
              message: "Please type first name.",
              trigger: "blur",
            },
          ],
          surname: [
            {
              required: true,
              message: "Please type last name.",
              trigger: "blur",
            },
          ],
          birthday: [
            {
              required: true,
              message: "Please select your birth month and year.",
              trigger: "blur",
            },
          ],
          email: [
            {
              type: "email",
              message: "Please type a valid email address.",
              trigger: "blur",
            },
          ],
          phone: [
            {
              required: true,
              message: "Please type a mobile number.",
              trigger: "blur",
            },
            {
              min: 10,
              max: 10,
              message:
                "Please type a valid Australian mobile number (10 digits).",
              trigger: "blur",
            },
          ],
          password: [
            {
              required: true,
              message: "Please type password.",
              trigger: "blur",
            },
            {
              min: 6,
              message:
                "Password must be longer than 6 alphanumeric characters.",
              trigger: "blur",
            },
          ],
          passwordConfirm: [
            {
              required: true,
              message: "Please re-type password.",
              trigger: "blur",
            },
            { validator: validateRetypePassword, trigger: "blur" },
          ],
          "shippingAddress.city": featureToggle.MEMBERHSIP
            ? [
                {
                  required: true,
                  message: "Please fill your suburb.",
                  trigger: "blur",
                },
              ]
            : [],
          "shippingAddress.zipCode": [
            {
              min: 4,
              max: 4,
              message: "Please type a valid Australian zip code (4 digits).",
              trigger: "blur",
            },
          ],
        },
      },

      amountFromDelivery: "0.00",
      currentOrderMode: null,
      isDelivery,

      currentTableNumber: Cookies.get(
        "kingdynasty_ec_home_options_table_number"
      ),
      currentMode: Cookies.get("kingdynasty_ec_home_options_selected_mode"),
    };
  },
  computed: {
    ...mapGetters([
      "shoppingCartId",
      "shoppingCart",
      "token",
      "customerId",
      "latestOrderId",
    ]),
  },
  beforeCreate() {
    if (this.$route.query.from === "qrcodescan") {
      log.info("Opening from table qr code scan");
      Cookies.set(
        "kingdynasty_ec_home_options_table_number",
        this.$route.query.tableNumber,
        { expires: new Date(Date.now() + 1000 * 60 * 30) }
      );
      Cookies.set(
        "kingdynasty_ec_home_options_selected_mode",
        this.$route.query.mode,
        { expires: new Date(Date.now() + 1000 * 60 * 30) }
      );
      window.location.href = "/?hideHomeOptions=true";
      return;
    }
    if (
      this.$route.query.preOrderLink === "true" &&
      this.$route.query.valid === "true" &&
      this.$route.query.reservationId
    ) {
      log.info("Opening from pre-order link or qrcode");
      Cookies.set(CONFIG_KEYS.selectedMode, "DINE_IN", {
        expires: new Date(Date.now() + 1000 * 60 * 30),
      });
      localStorage.setItem(
        CONFIG_KEYS.reservationId,
        this.$route.query.reservationId
      );
      window.location.href = "/?hideHomeOptions=true";
      return;
    }
    if (
      this.$route.query.orderDetailLink === "true" &&
      this.$route.query.valid === "false"
    ) {
      const errorCode = this.$route.query.error;
      log.info(`Invalid order detail link (${errorCode})`);
      if (ORDER_ERROR_CODE_MAP[errorCode]) {
        this.error = {
          title: "Invalid Order Link",
          message: ORDER_ERROR_CODE_MAP[errorCode],
        };
      }
      return;
    }
  },
  async created() {
    this.display.dialog.homeOptions.display = false;
    this.display.isLoading = true;
    log.info("Created", this.token, this.customerId, this.error);

    if (this.error && this.error.title && this.error.message) {
      this.display.dialog.error.isVisible = true;
      this.display.dialog.error.title = this.error.title;
      this.display.dialog.error.message = this.error.message;
    }

    if (!this.token && featureToggle.HOMEPAGE_SIGNUPIN_DIALOG) {
      this.display.dialog.signUpNotificationDialog.isVisible = true;
    }
    if (!this.shoppingCart.shoppingCartId) {
      log.info("Shopping cart not exist, creating one");
      await this.$store.dispatch("shoppingCart/createShoppingCart");
    } else {
      log.info(`Shopping cart exists, ID=${this.shoppingCart.shoppingCartId}`);
      await this._loadShoppingCart();
    }
    await this.initData();
    this.subtotal = calculateSubtotal(this.shoppingCart.shoppingCart.lineItems);
    this.amountFromDelivery = (
      MINIMUM_DELIVERY_AMOUNT - Number(this.subtotal)
    ).toFixed(2);
    this.display.isLoading = false;
    if (this.$route.query.hideHomeOptions !== "true") {
      Cookies.remove("kingdynasty_ec_home_options_selected_mode");
      this.display.dialog.homeOptions.isVisible = true;
      if (this.token) {
        const meCustomer = await getMe();
        this.display.dialog.homeOptions.memberName = [
          meCustomer.firstName,
          meCustomer.surname,
        ]
          .filter((n) => !!n)
          .join(" ");
      }
    }
    if (this.$route.query.showSignIn === "true" && !this.token) {
      this.display.dialog.homeOptions.isVisible = false;
      this.display.dialog.signUpIn.isVisible = true;
      this.display.dialog.signUpIn.mode = "SIGN_IN";
    }
    this.currentOrderMode = Cookies.get(
      "kingdynasty_ec_home_options_selected_mode"
    );
  },
  methods: {
    async initData() {
      const start = Date.now();
      const categories = await listCategories();
      if (isEmpty(categories)) {
        log.info("No category fetched, therefore no product");
        this.display.isLoading = false;
        return;
      }
      this.categories = categories;
      if (this.$route.query.selectedCategoryId) {
        const selectedCatIndex = this.categories.findIndex(
          (c) => c._id === this.$route.query.selectedCategoryId
        );
        await this.onClickCategory(selectedCatIndex);
      } else {
        this.activeCategoryName = this.categories[0].name;
        this.activeCategoryIndex = 0;
      }

      await this._loadProduct();
      log.info(`Catalog loaded in ${Date.now() - start}ms`);
    },

    async onClickCategory(index) {
      log.info(`Clicked on category ${index}`);
      this.isSearchMode = false;
      this.display.isLoading = true;
      this.activeCategoryIndex = index;
      this.activeChildCategoryIndex = 0;
      this.activeCategoryName = this.categories[index].name;
      await this._loadProduct();
      this._locateCategoryAnchor();
      this.display.isLoading = false;
    },
    onClickChildCategory(childCategoryIndex) {
      this.activeChildCategoryIndex = childCategoryIndex;
      this._locateCategoryAnchor();
    },
    _locateCategoryAnchor() {
      const activeCategory = this.categories[this.activeCategoryIndex];
      const activeChildCategory = anyPass([isNil, rIsEmpty])(
        activeCategory.children
      )
        ? null
        : activeCategory.children[this.activeChildCategoryIndex];
      const elementId = `#category${activeCategory._id}__childcategory${
        activeChildCategory ? activeChildCategory._id : null
      }`;
      this.$scrollTo(elementId, 500, {
        container: ".product-list",
      });
    },

    onClickDescriptionToggle(product) {
      product.isDescriptionExpanded = !product.isDescriptionExpanded;
    },

    async onChangeShoppingCartItemQuantity(event, product) {
      if (
        event > 0 &&
        (product.package.isPackage ||
          product.specification.isSpecificationEnabled ||
          (product.subProducts && product.subProducts.length > 0))
      ) {
        log.info("Adding a complex product, open up dialog");
        product.isAddedByIncrementButton = true;
        this._openComplexProductDialog(product);
        return;
      }
      await this._changeShoppingCartItemQuantity(product, Number(event), true);
    },
    onClickClosePackageOption() {
      const originalProduct = this.products.find(
        (product) => this.expandedProduct._id === product._id
      );
      if (
        this.expandedProduct.isAddedByIncrementButton &&
        !this.expandedProduct.isSelectionValid
      ) {
        originalProduct.shoppingCartItemQuantity = 0;
      }
      originalProduct.isAddedByIncrementButton = false;
      originalProduct.isSelectionValid = false;
      this.display.dialog.packageOptions.isVisible = false;
      this.display.dialog.packageOptions.currentTab = "Selections";
    },
    _openComplexProductDialog(product) {
      const lineItem = this.shoppingCart.shoppingCart.lineItems.find(
        (item) => item.productId === product._id
      );
      const productCopy = deepClone(product);
      this.display.dialog.packageOptions.isVisible = true;
      if (productCopy.package.isPackage) {
        this.display.dialog.packageOptions.currentTab = "Selections";
      } else if (productCopy.specification.isSpecificationEnabled) {
        this.display.dialog.packageOptions.currentTab = "Specifications";
      } else {
        this.display.dialog.packageOptions.currentTab = "Sub Menu";
      }

      if (productCopy.package.isPackage) {
        const productOptions = getAttributeValue(
          product.attributes,
          "packageOptions"
        );
        productCopy.packageSelections = (lineItem &&
          lineItem.packageSelections &&
          deepClone(lineItem.packageSelections)) || [
          productOptions.reduce(
            (current, incoming) =>
              Object.assign(current, {
                [incoming.name]: [],
              }),
            {}
          ),
        ];
        productOptions.forEach((opt) => {
          if (opt.isAllSelectedByDefault) {
            productCopy.packageSelections[0][opt.name] = opt.options;
          }
        });
      }

      if (productCopy.specification.isSpecificationEnabled) {
        productCopy.specificationSelection =
          (lineItem &&
            lineItem.specificationSelection &&
            deepClone(lineItem.specificationSelection)) ||
          {};
        productCopy.specificationSelectionIndex =
          lineItem && lineItem.specificationSelectionIndex;
      }

      if (isNotNilOrEmpty(productCopy.subProducts)) {
        productCopy.selectedSubProductNames = []; // TODO already selected sub menu
      }

      this.expandedProduct = productCopy;
    },
    isSelectionDisabled(productSelection, productOption, lineItemSelection) {
      const isUnlimitedSelection =
        !productSelection.numberOfSelection ||
        Number(productSelection.numberOfSelection) === 0;
      if (isUnlimitedSelection) {
        return false;
      }
      const isMaxItemSelected =
        !lineItemSelection[productSelection.name].includes(productOption) &&
        lineItemSelection[productSelection.name].length >=
          Number(productSelection.numberOfSelection);
      return productSelection.isAllSelectedByDefault || isMaxItemSelected;
    },
    async onClickComplexProductConfirm() {
      this.display.isLoading = true;
      try {
        const existingLineItem = this.shoppingCart.shoppingCart.lineItems.find(
          (item) => item.productId === this.expandedProduct._id
        );

        // validation
        if (this.expandedProduct.package.isPackage) {
          // selections validation
          const isSelectionValid = this.validateItemsAgainstSelection(
            this.expandedProduct
          );
          if (!isSelectionValid) {
            log.warn("Invalid selections");
            this.$message({
              type: "warning",
              message: "Please select for all sets.",
            });
            return;
          }
        }
        if (this.expandedProduct.specification.isSpecificationEnabled) {
          // specification validation
          const isSpecValid =
            isNotNilOrEmpty(this.expandedProduct.specificationSelectionIndex) &&
            this.expandedProduct.specificationSelectionIndex >= 0;
          if (!isSpecValid) {
            log.warn("Invalid specification");
            this.$message({
              message: "Please select specification.",
              type: "warning",
            });
            return;
          }
        }
        this.expandedProduct.isSelectionValid = true; // complex product valid for add

        const newItem = {
          productId: this.expandedProduct._id,
          quantity: 1,
          attributes: this.expandedProduct.attributes,
        };

        if (this.expandedProduct.package.isPackage) {
          if (existingLineItem) {
            setAttributeValue(
              existingLineItem.attributes,
              "packageSelections",
              this.expandedProduct.packageSelections
            );
          } else {
            newItem.attributes.push({
              key: "packageSelections",
              value: this.expandedProduct.packageSelections,
            });
          }
        }

        if (this.expandedProduct.specification.isSpecificationEnabled) {
          const specSelection = this.expandedProduct.specification
            .specificationOptions[
            this.expandedProduct.specificationSelectionIndex
          ];
          if (existingLineItem) {
            setAttributeValue(
              existingLineItem.attributes,
              "specificationSelectionIndex",
              this.expandedProduct.specificationSelectionIndex
            );
            setAttributeValue(
              existingLineItem.attributes,
              "specificationSelection",
              specSelection
            );
          } else {
            newItem.attributes.push({
              key: "specificationSelectionIndex",
              value: this.expandedProduct.specificationSelectionIndex,
            });
            newItem.attributes.push({
              key: "specificationSelection",
              value: specSelection,
            });
          }
        }

        let subItems = [];
        if (
          isNotNilOrEmpty(this.expandedProduct.subProducts) &&
          isNotNilOrEmpty(this.expandedProduct.selectedSubProductNames)
        ) {
          const selectedSubProducts = this.expandedProduct.subProducts.filter(
            (subProduct) =>
              this.expandedProduct.selectedSubProductNames.includes(
                `${subProduct.name} (${subProduct.unitNetPriceDisplay})`
              )
          );
          selectedSubProducts
            .map((subProduct) =>
              this.shoppingCart.shoppingCart.lineItems.findIndex(
                (item) => item.productId === subProduct._id
              )
            )
            .filter((index) => index >= 0)
            .forEach((index) => {
              this.shoppingCart.shoppingCart.lineItems.splice(index, 1);
            });
          subItems = selectedSubProducts.map((subProduct) => ({
            productId: subProduct._id,
            quantity: 1,
            parentProductId: this.expandedProduct._id,
          }));
        }

        if (existingLineItem) {
          const existingLineItemIndex = this.shoppingCart.shoppingCart.lineItems.findIndex(
            (item) => item.productId === existingLineItem.productId
          );
          subItems.forEach((subItem) => {
            this.shoppingCart.shoppingCart.lineItems.splice(
              existingLineItemIndex + 1,
              0,
              subItem
            );
          });
        } else {
          this.shoppingCart.shoppingCart.lineItems.push(newItem);
          subItems.forEach((subItem) =>
            this.shoppingCart.shoppingCart.lineItems.push(subItem)
          );
        }

        await this._postAddLineItem();
        this.display.dialog.packageOptions.isVisible = false;
      } catch (err) {
        log.error("Error when updating line item side selection", err);
        this.$message({
          type: "error",
          message: "Failed to update side selection",
        });
      } finally {
        this.display.isLoading = false;
      }
    },
    validateItemsAgainstSelection(product) {
      return (
        getAttributeValue(product.attributes, "packageOptions")
          .filter(
            (option) =>
              !!option.numberOfSelection && Number(option.numberOfSelection) > 0
          )
          .filter(
            (option) =>
              product.packageSelections[0][option.name].length !==
              Number(option.numberOfSelection)
          ).length === 0
      );
    },

    async onClickShoppingCart() {
      log.info("Go to shopping cart page");
      this.$router.push("shopping-cart");
    },

    async onClickCheckout() {
      if (
        MINIMUM_DELIVERY_AMOUNT - Number(this.subtotal) > 0 &&
        isDelivery(this.currentOrderMode)
      ) {
        log.info(`Cannot make delivery, current subtotal = ${this.subtotal}`);
        return this.$message(errorMessages.LESS_THAN_MINIMUM_DELIVERY_AMOUNT());
      }
      this.display.isLoading = true;
      if (this.shoppingCart.shoppingCart.lineItems.length <= 0) {
        log.info("No shopping cart item");
        this.display.isLoading = false;
        return this.$message({
          message:
            "Please consider to add some products to your shopping cart before checkout.",
          type: "error",
        });
      }
      if (this.latestOrderId) {
        log.info(
          `Latest order ${this.latestOrderId}, fetching to check if same day dine-in`
        );
        const latestOrder = await getById(this.latestOrderId);
        latestOrder.createdTimeDisplayDateOnly = moment(
          latestOrder.createdTime
        ).format("HH:mm:ss, D MMM YYYY");
        log.info("Latest order", latestOrder);
        if (this._isSameDayOngoingDineInOrder(latestOrder)) {
          log.info(
            `Latest order ${this.latestOrderId} is non-finalised dine-in`
          );
          this.currentDineInOrder = latestOrder;
          this.display.isLoading = false;
          this.display.dialog.dineInAddLineItemsConfirmation.isVisible = true;
          return;
        }
      }
      this._goCheckout();
    },
    async onClickAddToCurrentDineInOrder() {
      this.display.isLoading = true;
      await addLineItems(
        this.currentDineInOrder._id,
        this.shoppingCart.shoppingCart._id
      );
      await this.$store.dispatch("shoppingCart/createShoppingCart");
      this.$router.push("payment");
    },
    onClickPlaceNewOrder() {
      this._goCheckout();
    },
    _isSameDayOngoingDineInOrder(order) {
      return (
        getAttributeValue(order.attributes, "mode") === "DINE_IN" &&
        order.status !== "COMPLETED" &&
        utc(order.createdTime).isSame(utc(), "day")
      );
    },
    _goCheckout() {
      log.info("Go to checkout page");
      this.$router.push("order");
    },

    onClickMyOrder() {
      this.$router.push("orders");
    },
    onClickPersonal() {
      this.$router.push("personal");
    },
    onClickCustomerService() {
      log.info("Open customer service");
      this.display.dialog.customerService.isVisible = true;
    },
    onChangeSignInUpMode(event) {
      this.display.dialog.signUpIn.mode = SIGN_IN_UP_MODE.find(
        (m) => m.display === event
      ).value;
      this.customer = {
        firstName: "",
        surname: "",
        phone: "",
        password: "",
        passwordConfirm: "",
        shippingAddress: {
          state: "NSW",
          country: "Australia",
        },
      };
    },
    onClickSignUpIn() {
      log.info("Open signup/in");
      this.display.dialog.signUpIn.mode = "SIGN_IN";
      this.display.dialog.signUpIn.modeDisplay = SIGN_IN_UP_MODE.find(
        (m) => m.value === "SIGN_IN"
      ).display;
      this.customer = {
        firstName: "",
        surname: "",
        phone: "",
        password: "",
        passwordConfirm: "",
      };
      this.display.dialog.signUpIn.isVisible = true;
    },
    onClickSignUp() {
      this.$refs["customer"].validate(async (valid) => {
        if (!valid) {
          log.warn("Customer form invalid");
          return false;
        }
        this.display.isLoading = true;
        try {
          await signUp(this.customer);
          await this.$store.dispatch("customer/signIn", this.customer);
          this.display.dialog.signUpIn.isVisible = false;
        } catch (err) {
          log.warn("Error sign up", err);
          this.$message({
            message: "Sign up failed.",
            type: "error",
          });
        } finally {
          this.display.isLoading = false;
        }
      });
    },
    onClickSignIn() {
      this.$refs["customer"].validate(async (valid) => {
        if (!valid) {
          log.warn("Customer form invalid");
          return false;
        }
        this.display.isLoading = true;
        try {
          await this.$store.dispatch("customer/signIn", this.customer);
          this.display.dialog.signUpIn.isVisible = false;
        } catch (err) {
          log.warn("Error sign in", err);
          const message =
            err && err.response && err.response.status === 401
              ? "Incorrect username or password."
              : "Sign in failed.";
          this.$message({
            message,
            type: "error",
          });
        } finally {
          this.display.isLoading = false;
        }
      });
    },

    onClickDetailPage(product) {
      this.$router.push(`/product/${product._id}`);
    },

    onClickBooking() {
      this.display.isLoading = true;
      this.$router.push("/reservation");
    },

    onClickForgotPassword() {
      this.$router.push("/password-recovery");
    },

    async _postAddLineItem() {
      await this.$store.dispatch(
        "shoppingCart/updateShoppingCart",
        this.shoppingCart.shoppingCart
      );
      await this._loadShoppingCart();
      this._loadProductsAddedQuantities(this.products);
      this.subtotal = calculateSubtotal(
        this.shoppingCart.shoppingCart.lineItems
      );
    },

    async _changeShoppingCartItemQuantity(
      product,
      quantity,
      isUpdateShoppingCartImmediately
    ) {
      this.display.isLoading = true;
      try {
        product.shoppingCartItemQuantity = quantity;
        const shoppingCartItems = this.shoppingCart.shoppingCart.lineItems.filter(
          (item) => item.productId === product._id
        );

        const preparePackageSelections = (item) => {
          const productSelections = getAttributeValue(
            item.attributes,
            "packageOptions"
          );
          setAttributeValue(
            item.attributes,
            "packageSelections",
            Array(item.quantity)
              .fill(0)
              .map(() =>
                productSelections.reduce(
                  (current, incoming) =>
                    Object.assign(current, {
                      [incoming.name]: incoming.isAllSelectedByDefault
                        ? incoming.options
                        : [],
                    }),
                  {}
                )
              )
          );
        };
        if (shoppingCartItems.length > 0) {
          log.info(`Product ${product._id}, quantity ${quantity}`);
          shoppingCartItems.forEach((item) => {
            item.quantity = quantity;
            getAttributeValue(item.attributes, "isPackage") &&
              preparePackageSelections(item);
          });
        } else {
          log.info(`Adding product ${product._id} to shopping cart`);
          const newItem = {
            productId: product._id,
            quantity,
            attributes: product.attributes,
          };
          getAttributeValue(product.attributes, "isPackage") &&
            preparePackageSelections(newItem);
          this.shoppingCart.shoppingCart.lineItems.push(newItem);
        }
        this.shoppingCart.shoppingCart.lineItems = this.shoppingCart.shoppingCart.lineItems.filter(
          (item) => item.quantity > 0
        );
        if (isNotNilOrEmpty(product.subProducts) && quantity === 0) {
          // remove selected subproducts
          const subItems = this.shoppingCart.shoppingCart.lineItems.filter(
            (item) => item.parentProductId === product._id
          );
          subItems.forEach((subItem) => {
            const index = this.shoppingCart.shoppingCart.lineItems.findIndex(
              (item) => item._id === subItem._id
            );
            this.shoppingCart.shoppingCart.lineItems.splice(index, 1);
          });
        }
        if (isUpdateShoppingCartImmediately) {
          log.info(
            `Updating shopping cart ${this.shoppingCart.shoppingCartId} immediately`
          );
          await this.$store.dispatch(
            "shoppingCart/updateShoppingCart",
            this.shoppingCart.shoppingCart
          );
          await this._loadShoppingCart();
        }
        this.subtotal = calculateSubtotal(
          this.shoppingCart.shoppingCart.lineItems
        );
        this.amountFromDelivery = (
          MINIMUM_DELIVERY_AMOUNT - Number(this.subtotal)
        ).toFixed(2);
      } catch (err) {
        log.error(err);
        const { data, status } = err.response || {};
        if (status === 412) {
          // product sold out
          const { parameters } = data;
          this._refreshProductSoldOut(parameters);
          return this.$message({
            type: "error",
            message: "The product is sold out",
          });
        }
        this.$message({
          type: "error",
          message: `Unknown error (${err.message})`,
        });
      } finally {
        this.display.isLoading = false;
      }
    },
    _refreshProductSoldOut(soldOutProductIds) {
      this.products
        .filter((p) => soldOutProductIds.includes(p._id))
        .forEach((p) => {
          p.isSoldOut = true;
          setAttributeValue(p.attributes, "isSoldOut", true);
        });
    },

    async _loadProduct() {
      log.info(`Loading product of category ${this.activeCategoryName}`);
      const activeCategory = this.categories[this.activeCategoryIndex];
      const products = await listByCategory(this.activeCategoryName);
      this._loadProductsAddedQuantities(products);
      products.forEach(decorateProduct);
      products.forEach(this._loadProductDescriptionControl);
      if (isNil(activeCategory.children) || rIsEmpty(activeCategory.children)) {
        log.info(`Category ${activeCategory._id} does not have children`);
        return (this.products = products);
      }
      let childCategoryProducts = [];
      const childCategoryIdProductsMap = groupBy((p) => p.childCategoryId)(
        products
      );
      activeCategory.children.forEach((c) => {
        childCategoryProducts = childCategoryProducts.concat(
          childCategoryIdProductsMap[c._id]
        );
      });
      this.products = childCategoryProducts.filter((p) => !!p);
    },
    _loadProductDescriptionControl(product) {
      if (!!product.description && product.description.length > 50) {
        product.isDescriptionExpanded = false;
      }
    },
    _loadProductsAddedQuantities(products) {
      const productIdShoppingCartItemQuantity = this.shoppingCart.shoppingCart.lineItems.reduce(
        (current, incoming) =>
          Object.assign(current, { [incoming.productId]: incoming.quantity }),
        {}
      );
      products.forEach((product) => {
        product.shoppingCartItemQuantity =
          productIdShoppingCartItemQuantity[product._id] || 0;
      });
    },
    async _loadShoppingCart() {
      await this.$store.dispatch("shoppingCart/loadShoppingCart");
      this.shoppingCart.shoppingCart.lineItems.forEach(
        decorateShoppingCartItem
      );
    },
    _filterProductForDisplay() {
      const activeCategory = this.categories[this.activeCategoryIndex];
      this.productsShown = activeCategory.children.length
        ? this.products.filter(
            (p) =>
              p.childCategoryId ===
              activeCategory.children[this.activeChildCategoryIndex]._id
          )
        : this.products;
    },

    beforeSignUpInClose() {
      if (this.$route.query.hideHomeOptions !== "true") {
        this.display.dialog.homeOptions.isVisible = true;
        setTimeout(() => (this.display.dialog.signUpIn.isVisible = false), 150);
      } else {
        this.display.dialog.signUpIn.isVisible = false;
      }
    },
    onClickCovid19CheckIn() {
      if (!this.isCovidCheckInEnabled) {
        log.info("Covid check in is not enabled");
        return;
      }
      this.$router.push("/check-in-form");
    },
    onClickDineInQueue() {
      this.$router.push("/queue");
    },
    onClickTableReservation() {
      this.$router.push("/reservation");
    },
    onClickDineInOnlineOrder() {
      if (!this.isDineInEnabled) {
        log.info("Dine-in is not enabled");
        return;
      }
      Cookies.set("kingdynasty_ec_home_options_selected_mode", "DINE_IN");
      window.location.href = "/?hideHomeOptions=true";
    },
    onClickTakeawayOrder() {
      Cookies.set("kingdynasty_ec_home_options_selected_mode", "PICK_UP");
      window.location.href = "/?hideHomeOptions=true";
    },
    onClickDeliveryOrder() {
      Cookies.set("kingdynasty_ec_home_options_selected_mode", "TAKEAWAY");
      window.location.href = "/?hideHomeOptions=true";
    },
    onClickBecomeMember() {
      this.display.dialog.signUpIn.isVisible = true;
      this.display.dialog.signUpIn.mode = "SIGN_UP";
      this.display.dialog.signUpIn.modeDisplay = "Sign Up";
      setTimeout(
        () => (this.display.dialog.homeOptions.isVisible = false),
        150
      );
    },
    onClickWelcome() {
      if (!featureToggle.MEMBERSHIP) {
        return;
      }
      this.$router.push("/personal");
    },

    async onClickSearchOption() {
      if (this.isSearchMode) {
        log.info("Already in search mode");
        return;
      }
      this.display.isLoading = true;
      try {
        this.activeCategoryIndex = -1;
        this.isSearchMode = true;
        this.products = [];
        log.info("Loading all active products");
        const allProducts = await listActive();
        this._loadProductsAddedQuantities(allProducts);
        allProducts.forEach(decorateProduct);
        this.allProducts = allProducts;
      } catch (err) {
        log.error("Error when entering search mode", err);
      } finally {
        this.display.isLoading = false;
      }
    },
    onClickSearch() {
      if (!isNotNilOrEmpty(this.searchText) || this.searchText.length < 2) {
        this.$message({
          type: "error",
          message: "Please type more characters to search for dishes.",
        });
        return;
      }
      this.products = this.allProducts.filter((product) =>
        product.name.toLowerCase().includes(this.searchText.toLowerCase())
      );
    },
    onClickClearSearchText() {
      this.products = [];
    },
  },
};
</script>

<style scoped>
.category-list {
  padding-bottom: 5rem;
}

.product-list {
  padding-bottom: 15rem;
}

.category-list,
.product-list {
  -ms-overflow-style: none;
}

.category-list::-webkit-scrollbar,
.product-list::-webkit-scrollbar {
  display: none;
}

.category-list {
  height: 100vh;
  overflow: scroll;
}

.category-list__item {
  text-align: right;
  padding: 0.8rem;
  word-wrap: break-word;
}

.category-list__item.version {
  font-size: 0.7rem;
}

.product-list {
  height: 100vh;
  overflow: scroll;
}

.product-list__item__img {
  width: 100%;
}

.product-list__item >>> .el-card__header {
  padding: 0.5rem;
}

.product-list__item {
  margin: 0.5rem;
}

.product-list__item h3 {
  margin: 0;
}

.product-list__item__description,
.product-list__item__sku {
  font-size: 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
  hyphens: auto;
}

.product-list__item__description.clamped {
  line-clamp: 3;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  display: -webkit-box;
}

.product-list__item__description__toggle {
  font-size: 0.8rem;
}

.product-list__item__price {
  margin: 0 0 0.2rem;
  font-size: 1.2rem;
}

.product-list__item__sku {
  margin: 0.2rem 0 0;
}

.product-list__item__description {
  margin: 0.2rem 0;
}

.no-product-title {
  padding: 0 0 0.5rem 0.5rem;
  margin: 0;
}

.el-input-number.cart {
  width: 120px !important;
}

.el-input-number.cart >>> span {
  background-color: #221714 !important;
  border-radius: 0;
  height: 40px;
}

.el-input-number.cart >>> i {
  color: #c5b899 !important;
}

.el-input-number.cart >>> .el-input-number__decrease {
  left: 0 !important;
  top: 0 !important;
}

.el-input-number.cart >>> .el-input-number__increase {
  top: 0 !important;
}

.el-input-number.cart >>> .el-input-number__decrease.is-disabled,
.el-input-number.cart >>> .el-input-number__increase.is-disabled {
  opacity: 0.5;
}

.el-input-number.cart >>> input {
  width: 80px;
  border-color: #dcdfe6 !important;
  padding-left: 40px !important;
  padding-right: 0 !important;
}

.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 999;
}

.bottom-bar__col {
  padding: 0.5rem;
}

.bottom-bar__delivery-amount {
  font-size: 0.8rem;
}

.sign-up-in-dialog >>> .el-radio-group span {
  border-radius: 0 !important;
  border-color: #c5b899;
}

.sign-up-in-dialog >>> .el-radio-group .is-active span {
  background-color: #c5b899;
  box-shadow: none;
  color: #221714;
}

.customer-form {
  max-width: 992px;
  margin-left: auto;
  margin-right: auto;
}

.customer-form .el-col {
  padding: 0 0.6rem;
}

.customer-form >>> input {
  border: 1px solid #c5b899;
  border-radius: 0;
}

.side-btn {
  width: 6rem;
  padding: 0.5rem 0.6rem;
  position: fixed;
  right: 0;
  z-index: 10;
}

.side-btn-first {
  bottom: 10rem;
}

.side-btn-second {
  bottom: 8rem;
}

.side-btn-third {
  bottom: 6rem;
}

.side-btn-fourth {
  bottom: 4rem;
}

.el-checkbox >>> .el-checkbox__label,
.el-radio >>> .el-radio__label {
  color: #221714 !important;
}

.el-checkbox >>> .el-checkbox__input.is-checked .el-checkbox__inner,
.el-radio >>> .el-radio__input.is-checked .el-radio__inner {
  background-color: #c5b899;
  border-color: #c5b899;
}

.el-checkbox >>> .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: #dcdfe6;
}

.el-checkbox.is-checked >>> .el-checkbox__label,
.el-radio.is-checked >>> .el-radio__label {
  color: #c5b899 !important;
}

.side-dialog-selection {
  padding-bottom: 1rem;
}

.side-dialog-selection >>> label.el-checkbox,
.side-dialog-selection >>> label.el-radio {
  display: block;
  margin-bottom: 0.5rem;
}

.side-dialog-selection >>> .el-checkbox__label {
  white-space: normal;
}

.contact-row {
  margin: 1.5rem 0;
}

.child-category {
  font-size: 0.9rem;
}

.child-category.fade {
  opacity: 0.8;
}

.child-category:last-child {
  border-bottom: 4px solid #c5b899;
}

.footer-version {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: #fff;
  border-top: 1px solid #c5b899;
}

.add-item-dine-in-order-dialog .btn {
  max-width: 12rem;
  width: 100%;
  margin-top: 0.6rem;
}

.box-card {
  height: 5rem;
  margin-bottom: 2rem;
  font-weight: bold;
  font-size: 0.8rem;
}

.home-options-dlg * {
  word-break: keep-all;
}

.home-option-blk.disabled {
  opacity: 0.8;
}

.forgot-pwd {
  margin: 0 0.6rem 1rem 0.6rem;
}

.search-bar >>> .el-input__inner {
  border-radius: 0;
  border: none;
  border-right: 1px solid #c5b899;
  border-bottom: 1px solid #c5b899;
}

.search-btn {
  height: 40px;
}

.side-dialog-selection .el-radio-group {
  width: 100%;
}

.side-dialog-selection .el-radio-group >>> .el-radio__label {
  display: inline-block;
  white-space: normal;
  line-height: 1.2rem;
}

.complex-product-details >>> .el-tabs__active-bar {
  background-color: #c5b899;
}

.complex-product-details >>> .el-tabs__item.is-active {
  color: #c5b899;
  font-weight: bold;
}

@media (min-width: 992px) {
  /* :md */
  .product-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1440px) {
  /* between :lg and :xl */
  .product-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
  }
}

.error-dialog {
  z-index: 9999 !important;
}
</style>
